import BigNumber from 'bignumber.js'

import { NumericFormat } from '@blade/std-lib'

export const USD_WITH_LESS_THAN_1_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      exact: BigNumber(0),
      format: () => [{ type: 'literal', value: '$0' }],
    },
    {
      upperBound: 1,
      format: (input) => [
        { type: 'literal', value: input.lt(0) ? '-<$1' : '<$1' },
      ],
    },
    ...NumericFormat.PRESET_MAP['currency-amount-short'],
  ]

export const PNL_USD_PRESET = USD_WITH_LESS_THAN_1_PRESET

export const formatPnlUsd = (pnl: BigNumber) => {
  return NumericFormat.formatPreset(pnl, PNL_USD_PRESET, {
    currency: '$',
    forceSign: true,
  })
}

export const LIQUIDITY_BURNED_PRESET: Array<NumericFormat.NumericFormatRule> = [
  {
    exact: BigNumber(0),
    options: {},
  },
  {
    upperBound: 99,
    options: {
      mantissa: 0,
      roundingMode: 'ceil',
    },
  },
  {
    upperBound: Infinity,
    options: {
      mantissa: 0,
      roundingMode: 'floor',
    },
  },
]

export const formatLiquidityBurned = (percents: BigNumber.Value) => {
  return NumericFormat.formatPreset(
    BigNumber(percents),
    LIQUIDITY_BURNED_PRESET,
  )
}

export const MCAP_LIQ_VOL_SHORT_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      upperBound: 1000,
      options: {
        mantissa: 0,
        roundingMode: 'trunc',
      },
    },
    {
      upperBound: Infinity,
      options: {
        compact: true,
        maximumSignificantDigits: 3,
      },
    },
  ]

export const TX_COUNT_PRESET: Array<NumericFormat.NumericFormatRule> = [
  {
    upperBound: 10_000,
    options: {
      mantissa: 0,
    },
  },
  {
    upperBound: Infinity,
    options: {
      compact: true,
      mantissa: 1,
      trimMantissa: true,
      roundingMode: 'trunc',
    },
  },
]

export const formatMarketCapShort = (marketCap: BigNumber) => {
  return NumericFormat.formatPreset(marketCap, MCAP_LIQ_VOL_SHORT_PRESET, {
    currency: '$',
  })
}
export const formatLiquidityShort = (percents: BigNumber.Value) => {
  return NumericFormat.formatPreset(
    BigNumber(percents),
    MCAP_LIQ_VOL_SHORT_PRESET,
    {
      currency: '$',
    },
  )
}

export const PRICE_PRESET: Array<NumericFormat.NumericFormatRule> = [
  {
    upperBound: 0.1,
    options: {
      compactDecimalZeroes: true,
      compactDecimalZeroesThreshold: 3,
      compactDecimalZeroesRemainingPartLength: 2,
      mantissa: 4,
      trimMantissa: true,
    },
  },
  {
    upperBound: Infinity,
    options: {
      compact: true,
      maximumSignificantDigits: 3,
    },
  },
]

export const CURRENCY_AMOUNT_NATIVE_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      exact: BigNumber(0),
      options: {},
    },
    {
      upperBound: 0.001,
      format: (input) => [
        { type: 'literal', value: input.lt(0) ? '-<0.001' : '<0.001' },
      ],
    },
    {
      upperBound: 1,
      options: {
        mantissa: 3,
        trimMantissa: true,
      },
    },
    {
      upperBound: 100,
      options: {
        mantissa: 1,
        trimMantissa: true,
      },
    },
    {
      upperBound: Infinity,
      options: {
        mantissa: 0,
      },
    },
  ]

export const formatCurrencyAmountNative = (amount: BigNumber) => {
  return NumericFormat.formatPreset(amount, CURRENCY_AMOUNT_NATIVE_PRESET, {
    currency: undefined,
  })
}

export const COUNT_MAX_5_DIGITS_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      upperBound: 10_000,
      options: {
        mantissa: 1,
        compact: true,
        trimMantissa: true,
      },
    },
  ]

export const formatCountMax5Digits = (count: BigNumber) => {
  return NumericFormat.formatPreset(count, COUNT_MAX_5_DIGITS_PRESET)
}
export const COUNT_MAX_4_DIGITS_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      upperBound: 1000,
      options: {},
    },
    {
      upperBound: 10_000,
      options: {
        compact: true,
        mantissa: 2,
        trimMantissa: true,
      },
    },
    {
      upperBound: 100_000,
      options: {
        compact: true,
        mantissa: 1,
        trimMantissa: true,
      },
    },
    {
      upperBound: Infinity,
      options: {
        compact: true,
        mantissa: 1,
        trimMantissa: true,
      },
    },
  ]

export const formatCountMax4Digits = (count: BigNumber) => {
  return NumericFormat.formatPreset(count, COUNT_MAX_4_DIGITS_PRESET)
}

/**
 * The holder count, a digit finer than the other counts.
 *
 * Holders are the slowest figure on a card: the server recounts balances
 * about once every three minutes, and at three significant digits a pool
 * with 3,600 of them needs fifty more before `3.6K` becomes `3.65K`. The
 * count is followed trade by trade now, and this is so that following it
 * shows.
 */
export const HOLDERS_COUNT_PRESET: Array<NumericFormat.NumericFormatRule> = [
  {
    upperBound: 1000,
    options: {},
  },
  {
    upperBound: Infinity,
    options: {
      compact: true,
      maximumSignificantDigits: 4,
    },
  },
]

export const COUNT_MAX_3_DIGITS_PRESET: Array<NumericFormat.NumericFormatRule> =
  [
    {
      upperBound: 1000,
      options: {},
    },
    {
      upperBound: Infinity,
      options: {
        compact: true,
        maximumSignificantDigits: 3,
      },
    },
  ]

export const formatCountMax3Digits = (count: BigNumber) => {
  return NumericFormat.formatPreset(count, COUNT_MAX_3_DIGITS_PRESET)
}
